<script setup lang="ts">
  import { Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { useCloseOnEscape } from '@/entities/searchForm';
  import type { DepartureCity } from '@/shared/model';
  import MorphPopoverLayer from '@/shared/ui/MorphPopoverLayer.vue';
  import { DEPARTURE_CITY_TEXTS } from '../config/texts';

  interface DepartureCityDropdownProps {
    options: DepartureCity[];
    selectedId?: number | null;
    visible: boolean;
    dropdownTarget?: HTMLElement | null;
  }

  const props = withDefaults(defineProps<DepartureCityDropdownProps>(), {
    selectedId: null,
    dropdownTarget: null,
  });

  interface DepartureCityDropdownEmits {
    (e: 'select', city: DepartureCity): void;
    (e: 'close'): void;
  }

  const emit = defineEmits<DepartureCityDropdownEmits>();

  useCloseOnEscape(
    () => props.visible,
    () => emit('close'),
  );
</script>

<template>
  <MorphPopoverLayer
    :target="dropdownTarget"
    :visible="visible"
  >
    <div
      class="departure-city-dropdown"
      data-testid="departure-city-dropdown"
    >
      <div
        v-if="options.length === 0"
        class="departure-city-dropdown__empty"
        data-testid="departure-city-nothing-found"
      >
        <Typo
          :variant="TypoVariant.BodyLRegular"
          tag="span"
        >
          {{ DEPARTURE_CITY_TEXTS.nothingFound }}
        </Typo>
      </div>

      <template v-else>
        <button
          v-for="city in options"
          :key="city.id"
          type="button"
          class="departure-city-dropdown__option"
          :class="{ 'departure-city-dropdown__option_selected': selectedId === city.id }"
          data-testid="departure-city-dropdown-option"
          @click="emit('select', city)"
        >
          <Typo
            :variant="TypoVariant.BodyLRegular"
            tag="span"
          >
            {{ city.name }}
          </Typo>
        </button>
      </template>
    </div>
  </MorphPopoverLayer>
</template>

<style scoped lang="scss">
  .departure-city-dropdown {
    // Углы по фону городов обрезает сам список: карточка содержимое не обрезает, иначе обрежется список возраста в «Гостях».
    border-radius: var(--rounding-s);
    box-sizing: border-box;
    max-height: 360px;
    min-width: 240px;
    overflow-y: auto;
    width: var(--morph-popover-anchor-width, 100%);

    &__option {
      align-items: center;
      background: none;
      border: none;
      color: var(--color-text-primary);
      cursor: pointer;
      display: flex;
      min-height: 40px;
      padding: 0 var(--spacing-m);
      text-align: left;
      width: 100%;

      &_selected {
        background-color: var(--color-neutral-20);
      }

      &:hover {
        background-color: var(--color-bg-secondary);
      }
    }

    &__empty {
      color: var(--color-text-secondary);
      padding: var(--spacing-l) var(--spacing-m);
      text-align: center;
    }
  }
</style>
